import { Dialog, notifyError, notifySuccess } from '@/components'
import Map from '@/components/Organisms/Map/Map'
import { useUpdateTrafficDetectorMutation } from '@/modules/add-new-detector/services/updateTrafficDetectorService'
import { DIRECTION_ITEMS } from '@/modules/add-new-detector/Form'
import Input from '@/modules/organization/components/Input/Input'
import { UserTypes } from '@/types/typeUser'
import DeleteDeviceButton from '@/modules/device-module/components/deleteDeviceButton/deleteDeviceButton'
import { useChangeSet } from '@/utils/useChangeSet'
import { yupResolver } from '@hookform/resolvers/yup/dist/yup'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Typography } from '@mui/material'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as Yup from 'yup'
import MultipleSelect from '@/components/Molecules/MultipleSelect/MultipleSelect'

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required('Название не может быть пустым')
    .trim('Название не может быть пустым')
    .max(30, 'Поле должно содержать не более 30 символов'),
  address: Yup.string()
    .required('Адрес не может быть пустым')
    .trim('Адрес не может быть пустым')
    .max(250, 'Поле должно содержать не более 250 символов'),
  ip: Yup.string()
    .required('IP Адрес не может быть пустым')
    .trim('IP Адрес не может быть пустым')
    .matches(/(^(\d{1,3}\.){3}(\d{1,3})$)/, {
      message: 'Недопустимые символы',
      excludeEmptyString: true,
    })
    .test('ip', 'Адрес имеет некорректный формат', (value) => {
      return value === undefined || value.trim() === ''
        ? true
        : value.split('.').find((i) => parseInt(i, 10) > 255) === undefined
    })
    .max(15, 'IP Адрес должен содержать не более 15 символов'),
  portUi: Yup.string()
    .transform((v) => (v == null || v === undefined ? '' : String(v).trim()))
    .required('Порт не может быть пустым')
    .max(5, 'Порт не более 5 символов')
    .matches(/^\d+$/, 'Недопустимые символы')
    .test('port-range', 'Порт должен быть в диапазоне от 1 до 65535', (v) => {
      if (v == null || v === '') return false
      const n = Number(v)
      return Number.isInteger(n) && n >= 1 && n <= 65535
    }),
  portApi: Yup.string()
    .transform((v) => (v == null || v === undefined ? '' : String(v).trim()))
    .required('Порт API не может быть пустым')
    .max(5, 'Порт API должен содержать не более 5 символов')
    .matches(/^\d+$/, 'Недопустимые символы')
    .test(
      'port-api-range',
      'Порт API должен быть в диапазоне от 1 до 65535',
      (v) => {
        if (v == null || v === '') return false
        const n = Number(v)
        return Number.isInteger(n) && n >= 1 && n <= 65535
      }
    ),
  sameDirectionSensors: Yup.array()
    .min(1, 'Укажите попутное направление')
    .required('Укажите попутное направление'),
  oncomingDirection: Yup.array()
    .min(1, 'Укажите встречное направление')
    .required('Укажите встречное направление'),
})

export interface IDataFields {
  name: string
  address: string
  typeId: number
  ip: string
  portUi: string
  portApi: string
  sameDirectionSensors: number[]
  oncomingDirection: number[]
}

interface IUnitsFields {
  id?: number
  name: string
  address: string
  typeId: number
  ip: string
  portUi: number
  portApi: number
  latitude?: number
  longitude?: number
  sameDirectionSensors: number[]
  oppositeDirectionSensors: number[]
}

type Props = {
  formValues: IUnitsFields
  units: any
  refetch: () => void
  currentUserType: number
}
const FeaturesDetectorForm: React.FC<Props> = ({
  formValues,
  currentUserType,
  refetch,
  units,
}) => {
  const {
    name,
    address,
    typeId,
    ip,
    portUi,
    portApi,
    sameDirectionSensors,
    oppositeDirectionSensors,
  } = formValues
  const { id } = useParams()

  const defaultPoint = [47.2313, 39.7233]
  const [openDialogSave, setOpenDialogSave] = useState(false)
  const [formChanged, setFormChanged] = useState(false)
  const [newCoords, setNewCoords] = useState<number[]>([
    formValues.latitude ?? defaultPoint[0],
    formValues.longitude ?? defaultPoint[1],
  ])

  const handleChangeCoords = (coords: number[]) => {
    setNewCoords(coords)
    setFormChanged(true)
  }

  const defaultValues: IDataFields = {
    name: name ?? '',
    address: address ?? '',
    typeId,
    ip: ip ?? '',
    portUi: portUi != null ? String(portUi) : '',
    portApi: portApi != null ? String(portApi) : '',
    sameDirectionSensors: Array.isArray(sameDirectionSensors)
      ? sameDirectionSensors
      : [],
    oncomingDirection: Array.isArray(oppositeDirectionSensors)
      ? oppositeDirectionSensors
      : [],
  }

  const { handleSubmit, control, trigger, formState, watch, reset } =
    useForm<IDataFields>({
      defaultValues,
      resolver: yupResolver(validationSchema),
      criteriaMode: 'all',
      reValidateMode: 'onChange',
    })

  React.useEffect(() => {
    if (formValues?.id != null) {
      reset({
        name: formValues.name ?? '',
        address: formValues.address ?? '',
        typeId: formValues.typeId,
        ip: formValues.ip ?? '',
        portUi: formValues.portUi != null ? String(formValues.portUi) : '',
        portApi: formValues.portApi != null ? String(formValues.portApi) : '',
        sameDirectionSensors: formValues.sameDirectionSensors ?? [],
        oncomingDirection: formValues.oppositeDirectionSensors ?? [],
      })
      setNewCoords([
        formValues.latitude ?? defaultPoint[0],
        formValues.longitude ?? defaultPoint[1],
      ])
    }
  }, [
    formValues?.id,
    formValues?.name,
    formValues?.address,
    formValues?.ip,
    formValues?.portUi,
    formValues?.portApi,
    formValues?.sameDirectionSensors,
    formValues?.oppositeDirectionSensors,
    formValues?.latitude,
    formValues?.longitude,
    reset,
  ])

  const onSaveBtnClick = () => {
    setOpenDialogSave(true)
  }

  useChangeSet(() => {
    trigger().then((hasErrors: boolean) => {
      if (hasErrors) {
        setFormChanged(true)
      } else {
        setFormChanged(false)
      }
    })
  }, [
    watch('name'),
    watch('address'),
    watch('ip'),
    watch('portUi'),
    watch('portApi'),
    watch('sameDirectionSensors'),
    watch('oncomingDirection'),
  ])

  const [updateTrafficDetector] = useUpdateTrafficDetectorMutation()

  const onSubmit = async (dataForm: IDataFields) => {
    try {
      setOpenDialogSave(false)
      const coordinates = JSON.stringify(newCoords)
      const payload = {
        imageId: '',
        id: Number(id),
        name: dataForm.name,
        address: dataForm.address,
        coordinates,
        ip: dataForm.ip,
        portUi: Number(dataForm.portUi),
        portApi: Number(dataForm.portApi),
        sameDirectionSensors: dataForm.sameDirectionSensors,
        oppositeDirectionSensors: dataForm.oncomingDirection,
      }
      const res = await updateTrafficDetector(payload).unwrap()
      if (res.success) {
        notifySuccess('Данные успешно сохранены')
        refetch()
      }
    } catch (error) {
      console.error(error)
    }
  }

  const isSuperAdmin = currentUserType === UserTypes.superAdmin
  return (
    <>
      <Dialog
        message='Сохранить внесенные изменения?'
        open={openDialogSave}
        handleClose={() => setOpenDialogSave(false)}
        handleAgree={() => {
          if (Object.keys(formState.errors).length >= 1) {
            notifyError('Исправте ошибки в форме')
          }
          handleSubmit(onSubmit)()
        }}
      />

      <Box
        flex={1}
        display='flex'
        flexDirection={{ xss: 'column', md: 'row' }}
      >
        <Box
          flex={1}
          display='flex'
          border='1px solid #0072BC'
          mt={{ xss: 0, lg: '35px', xlgx: '-48px' }}
          height={{
            mdx: '100%',
            lg: 'calc(100% - 35px)',
            xlgx: 'calc(100% + 48px)',
          }}
        >
          <Map
            center={newCoords}
            setNewCoords={handleChangeCoords}
            dataUnits={units}
            newCoords={newCoords}
            draggable={isSuperAdmin}
          />
        </Box>
        <Box
          maxWidth={{ xss: '100%', md: 550 }}
          maxHeight={'calc(100vh - 245px)'}
          overflow={'auto'}
          display='flex'
          flex={{ xss: 1, xl4: 0.7, xll: 0.53 }}
        >
          <Box
            display='flex'
            flex={1}
            flexDirection={{ xss: 'column', md: 'column' }}
            pb={{ xss: 0.5, lg: 1 }}
          >
            <Box
              display='flex'
              flexDirection={{ xss: 'column', xlg: 'row' }}
            >
              <Box
                flex={1}
                px={1}
              >
                <Typography
                  variant='h6'
                  fontSize={18}
                  color='primary'
                >
                  Информация о системе
                </Typography>
                <Box
                  display='flex'
                  flexDirection={'column'}
                  gap={1.5}
                >
                  <Box
                    flex={1}
                    gap={1}
                    display='flex'
                    flexDirection={{ xss: 'column', md: 'row' }}
                  >
                    <Input
                      name='name'
                      type='text'
                      label='Имя'
                      control={control}
                      disabled={!isSuperAdmin}
                    />
                  </Box>
                  <Box
                    flex={1}
                    gap={1}
                    display='flex'
                    flexDirection={{ xss: 'column', md: 'row' }}
                  >
                    <Input
                      name='address'
                      type='text'
                      label='Адрес'
                      control={control}
                      disabled={!isSuperAdmin}
                    />
                  </Box>

                  <Box
                    flex={1}
                    gap={1}
                    display='flex'
                    flexDirection={{ xss: 'column', md: 'row' }}
                  >
                    <Input
                      name='ip'
                      type='text'
                      label='IP адрес'
                      control={control}
                      disabled={!isSuperAdmin}
                    />
                  </Box>
                  <Box
                    flex={1}
                    gap={1}
                    display='flex'
                    flexDirection={{ xss: 'column', md: 'row' }}
                  >
                    <Input
                      name='portUi'
                      type='text'
                      label='Порт'
                      control={control}
                      disabled={!isSuperAdmin}
                    />
                  </Box>
                  <Box
                    flex={1}
                    gap={1}
                    display='flex'
                    flexDirection={{ xss: 'column', md: 'row' }}
                  >
                    <Input
                      name='portApi'
                      type='text'
                      label='Порт API'
                      control={control}
                      disabled={!isSuperAdmin}
                    />
                  </Box>
                  <Box
                    flex={1}
                    gap={1}
                    display='flex'
                    flexDirection={{ xss: 'column', md: 'row' }}
                  >
                    <MultipleSelect
                      label={'Попутное направление'}
                      control={control as any}
                      name='sameDirectionSensors'
                      items={DIRECTION_ITEMS}
                      disabledValues={watch('oncomingDirection')}
                      disabled={!isSuperAdmin}
                    />
                    <MultipleSelect
                      label={'Встречное направление'}
                      control={control as any}
                      name='oncomingDirection'
                      items={DIRECTION_ITEMS}
                      disabledValues={watch('sameDirectionSensors')}
                      disabled={!isSuperAdmin}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            {isSuperAdmin && (
              <Box
                display='flex'
                alignItems='flex-start'
                justifyContent='flex-end'
                gap={2}
                mr={{ xss: 0.5, lg: 2 }}
                pt={5}
              >
                <Button
                  variant='contained'
                  color='success'
                  target={'_blank'}
                  href={
                    formValues?.ip
                      ? `http://${formValues?.ip}:${formValues?.portUi}`
                      : '/'
                  }
                >
                  НАСТРОЙКИ
                </Button>
                {id && <DeleteDeviceButton deviceId={id} />}
                <LoadingButton
                  variant='outlined'
                  onClick={onSaveBtnClick}
                  disabled={!formChanged}
                >
                  <span>Сохранить</span>
                </LoadingButton>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </>
  )
}

export default FeaturesDetectorForm
